import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import ClientDoc from 'src/models/ClientDoc';
import { BiDownload } from 'react-icons/bi';
import { FaTrash } from 'react-icons/fa';

interface IClientDocumentManagementDialogProps {
    dialogName: string;
    clientNumber?: number;
    documentList?: Array<ClientDoc>;
    addDocument?: (description: string, file: File) => void;
    deleteDocument?: (docId: number) => void;
    downloadDocument?: (doc: ClientDoc) => void;
    hideDialog: () => void;
}

// Port of CL_PR.fmt's client Documents window (client_docs, CD_TYPE=1) - same overall dialog
// shape/layout as InvoiceDocumentManagementDialog.tsx (title bar, fixed-layout table, per-row
// icon buttons, centered Close), but without any of Invoice's merge/archive/document-type-lookup
// machinery - the Oracle source's own Client Documents window is just a plain list with add/
// download/delete, so the inline Description+file-picker "Add Document" row is borrowed from
// VendorDocumentManagementDialog.tsx instead (Invoice's own Add flow is a separate dialog that
// doesn't apply here).
const ClientDocumentManagementDialog = (props: IClientDocumentManagementDialogProps) => {
    const { register, getValues, reset } = useForm();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
    };

    const handleAddDocument = () => {
        if (!selectedFile) {
            return;
        }

        const description = getValues()?.documentDescription;
        props?.addDocument?.(description, selectedFile);
        setSelectedFile(null);
        reset({ documentDescription: '' });
    };

    const handleClose = () => {
        props.hideDialog();
    };

    return (
        <Dialog title="Manage Client Documents" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '50%' }}>
            <div style={{ display: 'flex', justifyContent: 'left', width: '100%' }}>
                <form style={{ width: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {!!props?.clientNumber && (
                            <label>Client #:<span style={{ fontWeight: 'bold', fontSize: '15px' }}>{` ` + props.clientNumber}</span></label>
                        )}
                        {/* .project-billing's own table CSS sets display:block, which breaks the
                            browser's normal table column-alignment - overridden back to a real
                            fixed-layout table here, same as InvoiceDocumentManagementDialog.tsx/
                            VendorDocumentManagementDialog.tsx. */}
                        <table className={'project-billing'} style={{ display: 'table', tableLayout: 'fixed', width: '100%' }}>
                            <thead style={{ textAlign: 'center' }}>
                                <tr>
                                    <th style={{ width: '36%' }}>Description</th>
                                    <th style={{ width: '26%' }}>File Name</th>
                                    <th style={{ width: '9%' }}>Type</th>
                                    <th style={{ width: '16%' }}>Date</th>
                                    <th style={{ width: '13%' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {props?.documentList && props.documentList.length > 0 && props.documentList.map((doc: ClientDoc, index: number) => (
                                    <tr key={doc.id ?? index} className="table-item-hover" style={{ textAlign: 'center' }}>
                                        <td
                                            title={doc.description}
                                            style={{ width: '36%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {doc.description}
                                        </td>
                                        <td
                                            title={doc.originalFileName}
                                            style={{ width: '26%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {doc.originalFileName}
                                        </td>
                                        <td style={{ width: '9%' }}>{doc.documentType}</td>
                                        <td style={{ width: '16%' }}>{doc?.fileDate ? new Date(doc.fileDate).toLocaleDateString() : ''}</td>
                                        <td style={{ width: '13%' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '.25rem', marginBottom: '1rem' }}>
                                                <Button
                                                    style={{ height: '2.2rem', background: 'transparent' }}
                                                    text={''}
                                                    title="Download"
                                                    reactIcon={BiDownload}
                                                    iconHoverColor="blue"
                                                    iconStyle={{ fill: 'rgb(99, 147, 193)', width: '1.6rem', height: '1.6rem', verticalAlign: 'center' }}
                                                    onClick={() => props?.downloadDocument(doc)}
                                                />
                                                <Button
                                                    style={{ height: '2.2rem', background: 'transparent' }}
                                                    text={''}
                                                    title="Delete"
                                                    reactIcon={FaTrash}
                                                    iconHoverColor="blue"
                                                    iconStyle={{ fill: 'rgb(237, 53, 36)', width: '1.6rem', height: '1.6rem', verticalAlign: 'center' }}
                                                    onClick={() => props?.deleteDocument(doc.id)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {(!props?.documentList || props.documentList.length === 0) && (
                                    <tr style={{ height: '2rem' }}>
                                        <td colSpan={5} style={{ textAlign: 'center' }}>No documents</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '1rem' }}>
                            <RHFInputBox
                                labelOnLeft={true}
                                label={'Description'}
                                name={'documentDescription'}
                                register={register}
                                style={{ width: '100%' }}
                                bodyStyle={{ width: '50%' }}
                            />
                            <input type="file" onChange={handleFileChange} style={{ marginLeft: '1rem' }} />
                            <Button
                                className="blue-hover-button"
                                style={{ width: '15%', marginLeft: '1rem' }}
                                text={'Add Document'}
                                disabled={!selectedFile}
                                onClick={() => handleAddDocument()}
                            />
                        </div>
                    </div>
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Close'}
                    onClick={() => handleClose()}
                />
            </div>
        </Dialog>
    );
};

export default ClientDocumentManagementDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/ClientDocumentManagementDialog.tsx